import { FC, useEffect, useState } from 'react';
import { StandardDropDownItem } from '@groove/ui/Components/DropDownList';
import parsePhoneNumber from 'libphonenumber-js';
import PicklistNew from '@groove/ui/Components/PicklistNew';
import { CopyIcon, PhoneIcon } from '@groove/ui/Components/BoogieIcon';
import cx from 'classnames';
import { Callout } from '@fluentui/react';
import Tooltip from '@groove/ui/Components/Tooltip';
import Text from '@groove/ui/Components/Text';
import { motion } from 'framer-motion';
import LinkButton from '@groove/ui/Components/LinkButton';
import useUserGet from '@groove/api/hooks/useUserGet';

import useStore from '../store/useStore';

import RecipientSelector from './RecipientSelector';

const PhoneRecipient: FC = () => {
  const [phoneItems, setPhoneItems] = useState<StandardDropDownItem[]>([]);
  const [selectedPhoneItem, setSelectedPhoneItem] = useState<
    StandardDropDownItem[]
  >([]);
  const [showCopiedTip, setShowCopiedTip] = useState(false);

  const who = useStore(store => store.action.who);
  const id = useStore(store => store.action.id);
  const isTaskPaneOpenRaw = useStore(store => store.otherValues.isTaskPaneOpen);
  const taskPaneInitialClose = useStore(
    store => store.otherValues.taskPaneInitialClose,
  );
  const isTaskPaneOpen = isTaskPaneOpenRaw && !taskPaneInitialClose;

  const { data: user } = useUserGet();

  useEffect(() => {
    let copyAnimation: NodeJS.Timeout;
    if (showCopiedTip) {
      copyAnimation = setTimeout(() => setShowCopiedTip(false), 4000);
    }
    return () => {
      if (copyAnimation) clearTimeout(copyAnimation);
    };
  }, [showCopiedTip]);

  useEffect(() => {
    if (who && who.phone) {
      const recipient = useStore.getState().action.toRecipients?.[0];
      const selectedPhoneNumber = recipient?.phone;

      const tempPhoneItems: StandardDropDownItem[] = [];
      if (who.phones?.length) {
        who.phones.forEach(item => {
          tempPhoneItems.push({
            key: item.field,
            text:
              parsePhoneNumber(item.value)?.formatInternational() || item.value,
            secondaryText: [item.label],
            value: item.value,
          });
        });
      } else {
        tempPhoneItems.push({
          key: 'phone',
          text:
            parsePhoneNumber(who.phone || '')?.formatInternational() ||
            who.phone,
          secondaryText: ['Phone'],
          value: who.phone,
        });

        if (who.mobilePhone) {
          tempPhoneItems.push({
            key: 'mobile_phone',
            text:
              parsePhoneNumber(who.mobilePhone)?.formatInternational() ||
              who.mobilePhone,
            secondaryText: ['Mobile Phone'],
            value: who.mobilePhone,
          });
        }
      }

      setPhoneItems(tempPhoneItems);

      if (selectedPhoneNumber) {
        setSelectedPhoneItem([
          {
            key: recipient.phoneField || 'phone',
            text:
              parsePhoneNumber(
                selectedPhoneNumber || '',
              )?.formatInternational() ||
              selectedPhoneNumber ||
              '',
            secondaryText: ['Phone'],
            value: selectedPhoneNumber || '',
          },
        ]);
      } else {
        setSelectedPhoneItem([tempPhoneItems[0]]);
        const toRecipients = [
          {
            id: who.id.toString(),
            email: who.email,
            name: who.name,
            phone: tempPhoneItems[0].value as string,
            phoneField: tempPhoneItems[0].key as string,
            attributes: {
              type: who.sfdcType,
            },
            sfdcId: who.sfdcId,
          },
        ];
        useStore.getState().updateAction({
          toRecipients,
        });
        useStore.getState().updateOriginalAction({
          toRecipients,
        });
      }
    } else {
      setPhoneItems([]);
      setSelectedPhoneItem([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [who?.id, id, who?.phone, who?.mobilePhone, who?.name, who?.phones]);

  const onNumberChange = (item: StandardDropDownItem | null): void => {
    if (item && who) {
      const { toRecipients } = useStore.getState().action;
      let tempToRecipients = [...(toRecipients || [])];
      const index = tempToRecipients.findIndex(
        record => record.id === who.sfdcId || record.sfdcId === who.sfdcId,
      );
      const recipient = {
        ...tempToRecipients[index],
        id: who.id.toString(),
        sfdcId: who.sfdcId,
        phone: item.value as string,
        phoneField: item.key as string,
      };

      tempToRecipients = [
        ...tempToRecipients.slice(0, index),
        { ...recipient },
        ...tempToRecipients.slice(index + 1),
      ];

      useStore.getState().updateAction({
        toRecipients: tempToRecipients,
      });
      setSelectedPhoneItem([item]);
    }
  };

  const onCopyClick = (): void => {
    if (selectedPhoneItem?.[0]?.value) {
      navigator.clipboard.writeText(selectedPhoneItem[0].value.toString());
      setShowCopiedTip(true);
    }
  };

  const renderTo = (
    <RecipientSelector className="flex-1 mr-[4px] min-w-0" placeholder="To" />
  );

  let phonePlaceholder = 'Phone';

  if (selectedPhoneItem.length === 0) {
    if (who) phonePlaceholder = 'No phone number';
    else if (phoneItems.length < 1) phonePlaceholder = 'No phone numbers';
    else phonePlaceholder = 'Please add a recipient';
  }

  const renderPhone = (
    <>
      <PicklistNew
        className="flex-1 mx-[4px] min-w-0"
        onChange={onNumberChange}
        items={phoneItems}
        selectedValues={selectedPhoneItem}
        placeholder={phonePlaceholder}
        disabled={!who || phoneItems.length < 1}
      />
      <div
        className={cx(
          'px-[8px] items-center flex h-full mr-[4px]',
          selectedPhoneItem?.[0]?.value
            ? 'text-wavyNavy/900 cursor-pointer'
            : 'text-neutral/400 cursor-not-allowed',
        )}
        onClick={onCopyClick}
        tabIndex={0}
        aria-label="Copy Button"
        role="button"
        onKeyDown={onCopyClick}
        id="copy-button"
      >
        <CopyIcon className="w-[12px]" />
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: showCopiedTip ? 1 : 0 }}
        transition={{ duration: 0.1 }}
      >
        {showCopiedTip && (
          <Callout
            role="alert"
            gapSpace={-5}
            target="#copy-button"
            className="p-[8px]"
          >
            <Text className="text-body-sm">Copied to clipboard!</Text>
          </Callout>
        )}
      </motion.div>
      <LinkButton
        href={
          user?.data.settings?.is_3c_logic_dialer_enabled
            ? undefined
            : `tel:${selectedPhoneItem?.[0]?.text}`
        }
        onClick={
          user?.data.settings?.is_3c_logic_dialer_enabled
            ? () => {
                (
                  document.querySelector(
                    'iframe.openctiSoftPhone',
                  ) as HTMLIFrameElement
                )?.contentWindow?.postMessage(
                  {
                    apiVersion: 50,
                    apiType: 'opencti',
                    methodName: 'onClickToDial',
                    callId: 1,
                    response: {
                      number: selectedPhoneItem?.[0]?.text,
                      recordId: who?.id.toString(),
                      objectType: who?.id.toString()?.startsWith('003')
                        ? 'Contact'
                        : 'Lead',
                    },
                    shouldExecuteCallback: true,
                    shouldExecuteListener: true,
                  },
                  '*',
                );
              }
            : undefined
        }
        className={cx(
          'pl-[4px] mr-[4px]',
          selectedPhoneItem?.[0]?.value
            ? 'text-wavyNavy/900 cursor-pointer'
            : 'text-neutral/400 cursor-not-allowed',
        )}
      >
        <Tooltip
          content={
            selectedPhoneItem?.[0]?.value
              ? `Call ${selectedPhoneItem?.[0]?.text}}`
              : 'Please have a valid phone number selected'
          }
        >
          <PhoneIcon
            className={cx(
              'w-[16px]',
              selectedPhoneItem?.[0]?.value
                ? 'text-wavyNavy/900 cursor-pointer'
                : 'text-neutral/400 cursor-not-allowed',
            )}
          />
        </Tooltip>
      </LinkButton>
    </>
  );

  let renderBoth = (
    <>
      {renderTo}
      {renderPhone}
    </>
  );

  if (!isTaskPaneOpen)
    renderBoth = (
      <>
        <div className="flex flex-row flex-1 items-center min-w-0">
          {renderTo}
        </div>
        <div className="flex flex-row flex-1 items-center min-w-0">
          {renderPhone}
        </div>
      </>
    );

  return <div className="flex flex-row items-center">{renderBoth}</div>;
};

export default PhoneRecipient;
